/**
 * The API surface the screens use.
 *
 * Each function is one of three things, and says which:
 *
 *   LIVE     — the endpoint exists today and is always called for real.
 *   MOCKED   — the endpoint does not exist. `MOCK_MODE` decides; the live call
 *              is written out in full beside the mock, so swapping is deleting
 *              the mock branch. Nothing else in the app changes.
 *   MIXED    — a real mutation with no real list to drive it, so mock mode
 *              mutates the in-memory fixture instead.
 *
 * No screen imports `mock.ts`. That boundary is what makes the swap a one-file
 * change rather than a sweep.
 */
import { http } from './http.ts';
import { currentOperator } from './auth.ts';
import * as mock from './mock.ts';
import type {
  AdminDeposit,
  AiAskResult,
  AiRunRow,
  AuditEntry,
  DailySeries,
  DepositFunnel,
  PlayerLifetime,
  ReconciliationReport,
  ReconImportResult,
  RetentionCohort,
  AdminEdgeApplied,
  AdminEdgePlan,
  AdminGame,
  AdminPaymentMethod,
  AdminWithdrawal,
  ConfirmDepositResult,
  ExpiryResult,
  GameHold,
  GameSettingsPatch,
  LedgerEntry,
  MetricsWindow,
  Overview,
  PaymentLimits,
  PaymentMethodCreate,
  PaymentMethodPatch,
  PaymentMethodRegistry,
  ApproveOutcome,
  MfaEnrollment,
  MfaStatus,
  PlatformSetting,
  PlayerSummary,
  PromoCampaign,
  PromoCampaignCreate,
  PromoCampaignPatch,
  PromoCode,
  PromoLiability,
  PromoWeight,
  RejectedWithdrawal,
  VipTier,
  VipTierUpsert,
  SecretSetResult,
  SecretValidation,
  SecretVersion,
  SecretView,
  UserStatus,
  WithdrawalStatus,
  WithdrawalView,
} from './types.ts';

export { LIVE_ENDPOINTS, MOCK_MODE } from './mock.ts';

// ----------------------------------------------------------------- dashboard

/** MOCKED — `GET /internal/admin/metrics/overview`. */
export async function fetchOverview(window: MetricsWindow): Promise<Overview> {
  if (mock.MOCK_MODE) return mock.settle(mock.overviewFor(window));
  return http.get<Overview>('/internal/admin/metrics/overview', { window });
}

/** MOCKED — `GET /internal/admin/metrics/games`. */
export async function fetchGameHold(window: MetricsWindow): Promise<GameHold[]> {
  if (mock.MOCK_MODE) return mock.settle(mock.gameHold);
  const body = await http.get<{ games: GameHold[] }>('/internal/admin/metrics/games', { window });
  return body.games;
}

// ------------------------------------------------------------------ deposits

/** MOCKED — `GET /internal/admin/deposits?status=…`. */
export async function fetchDeposits(status?: string): Promise<AdminDeposit[]> {
  if (mock.MOCK_MODE) {
    const rows = status ? mock.deposits.filter((d) => d.status === status) : mock.deposits;
    return mock.settle(rows);
  }
  const body = await http.get<{ deposits: AdminDeposit[] }>('/internal/admin/deposits', {
    ...(status === undefined ? {} : { status }),
  });
  return body.deposits;
}

/**
 * LIVE — `POST /internal/payments/deposits/confirm`.
 *
 * In mock mode the same call is simulated against the fixture, because
 * confirming a deposit that only exists in the browser cannot reach the server.
 * The live branch is the one that matters and is unconditional once the listing
 * endpoint above exists.
 */
export async function confirmDeposit(input: {
  utr: string;
  reference?: string;
  narration?: string;
  observedAmount?: string;
}): Promise<ConfirmDepositResult> {
  if (mock.MOCK_MODE) {
    const row = mock.deposits.find((d) => d.reference === input.reference);
    if (!row) throw new Error('No pending deposit matches that reference.');
    row.status = 'credited';
    row.utrReference = input.utr;
    row.creditedAt = new Date().toISOString();
    const player = mock.playerById(row.userId);
    return mock.settle({
      depositId: row.depositId,
      userId: row.userId,
      status: 'credited' as const,
      creditedAmount: input.observedAmount ?? row.amount,
      balance: player?.balance ?? '0.0000',
      alreadyCredited: false,
    });
  }
  return http.post<ConfirmDepositResult>('/internal/payments/deposits/confirm', {
    utr: input.utr,
    ...(input.reference === undefined ? {} : { reference: input.reference }),
    ...(input.narration === undefined ? {} : { narration: input.narration }),
    ...(input.observedAmount === undefined ? {} : { observedAmount: input.observedAmount }),
  });
}

/** LIVE — `POST /internal/payments/deposits/expire`. */
export async function expireStaleDeposits(): Promise<ExpiryResult> {
  if (mock.MOCK_MODE) {
    const now = Date.now();
    let expired = 0;
    for (const d of mock.deposits) {
      if (d.status === 'pending' && new Date(d.expiresAt).getTime() < now) {
        d.status = 'expired';
        expired += 1;
      }
    }
    return mock.settle({ expired, headroomReleased: '0.0000' });
  }
  return http.post<ExpiryResult>('/internal/payments/deposits/expire');
}

// --------------------------------------------------------------- withdrawals

/** MOCKED — `GET /internal/admin/withdrawals?status=…`. */
export async function fetchWithdrawals(status?: WithdrawalStatus): Promise<AdminWithdrawal[]> {
  if (mock.MOCK_MODE) {
    const rows = status ? mock.withdrawals.filter((w) => w.status === status) : mock.withdrawals;
    return mock.settle(rows);
  }
  const body = await http.get<{ withdrawals: AdminWithdrawal[] }>('/internal/admin/withdrawals', {
    ...(status === undefined ? {} : { status }),
  });
  return body.withdrawals;
}

/**
 * LIVE — `POST /internal/payments/withdrawals/:id/approve`.
 *
 * Note what the body does NOT carry: an operator id. Identity comes from the
 * verified token; the server rejects any body that still smuggles one in.
 */
export async function approveWithdrawal(id: number, note?: string): Promise<ApproveOutcome> {
  if (mock.MOCK_MODE) return mock.settle(mock.approveMock(id, currentOperator()?.id ?? 0, note));
  return http.post<ApproveOutcome>(`/internal/payments/withdrawals/${id}/approve`, {
    ...(note === undefined ? {} : { note }),
  });
}

/** LIVE — `POST /internal/payments/withdrawals/:id/paid`. */
export async function markWithdrawalPaid(id: number, note?: string): Promise<WithdrawalView> {
  if (mock.MOCK_MODE) return mock.settle(transitionMock(id, 'paid', note));
  return http.post<WithdrawalView>(`/internal/payments/withdrawals/${id}/paid`, {
    ...(note === undefined ? {} : { note }),
  });
}

/**
 * LIVE — `POST /internal/payments/withdrawals/:id/reject`.
 * The note is mandatory here, both in the contract and in the UI: a rejection
 * refunds the player and they are owed a reason.
 */
export async function rejectWithdrawal(id: number, note: string): Promise<RejectedWithdrawal> {
  if (mock.MOCK_MODE) {
    const view = transitionMock(id, 'rejected', note);
    const player = mock.playerById(mock.withdrawals.find((w) => w.withdrawalId === id)?.userId ?? 0);
    return mock.settle({ ...view, balance: player?.balance ?? '0.0000', refunded: true });
  }
  return http.post<RejectedWithdrawal>(`/internal/payments/withdrawals/${id}/reject`, { note });
}

function transitionMock(id: number, to: WithdrawalStatus, note?: string): WithdrawalView {
  const row = mock.withdrawals.find((w) => w.withdrawalId === id);
  if (!row) throw new Error('Withdrawal not found.');
  row.status = to;
  row.reviewedBy = currentOperator()?.id ?? 0;
  if (note !== undefined) row.reviewNote = note;
  if (to === 'paid') row.paidAt = new Date().toISOString();
  return {
    withdrawalId: row.withdrawalId,
    amount: row.amount,
    fee: row.fee,
    netAmount: row.netAmount,
    status: row.status,
    destination: row.destination,
    reviewNote: row.reviewNote,
    paidAt: row.paidAt,
    createdAt: row.createdAt,
  };
}

// ------------------------------------------------------------------- players

/** MOCKED — `GET /internal/admin/players?q=…`. */
export async function fetchPlayers(query: string): Promise<PlayerSummary[]> {
  if (mock.MOCK_MODE) {
    const q = query.trim().toLowerCase();
    const rows = q
      ? mock.players.filter(
          (p) =>
            p.username.toLowerCase().includes(q) ||
            String(p.userId) === q ||
            (p.email ?? '').toLowerCase().includes(q) ||
            (p.phone ?? '').includes(q),
        )
      : mock.players;
    return mock.settle(rows);
  }
  const body = await http.get<{ players: PlayerSummary[] }>('/internal/admin/players', {
    ...(query.trim() === '' ? {} : { q: query.trim() }),
  });
  return body.players;
}

/** MOCKED — `GET /internal/admin/players/:id`. */
export async function fetchPlayer(userId: number): Promise<PlayerSummary | null> {
  if (mock.MOCK_MODE) return mock.settle(mock.playerById(userId) ?? null);
  return http.get<PlayerSummary>(`/internal/admin/players/${userId}`);
}

/**
 * MOCKED — `GET /internal/admin/players/:id/statement`.
 *
 * `GET /wallet/statement` exists but is player-scoped: identity comes from the
 * access token and no endpoint accepts a userId. An operator therefore cannot
 * read anyone's statement through it, by design. This needs its own route.
 */
export async function fetchStatement(userId: number, limit = 50): Promise<LedgerEntry[]> {
  if (mock.MOCK_MODE) return mock.settle(mock.statementFor(userId));
  const body = await http.get<{ entries: LedgerEntry[] }>(
    `/internal/admin/players/${userId}/statement`,
    { limit },
  );
  return body.entries;
}

/** MOCKED — `PATCH /internal/admin/players/:id`. */
export async function setPlayerStatus(
  userId: number,
  status: UserStatus,
  reason: string,
): Promise<PlayerSummary> {
  if (mock.MOCK_MODE) {
    const row = mock.playerById(userId);
    if (!row) throw new Error('Player not found.');
    row.status = status;
    return mock.settle(row);
  }
  return http.patch<PlayerSummary>(`/internal/admin/players/${userId}`, { status, reason });
}

// --------------------------------------------------------------------- games

/** MOCKED — `GET /internal/admin/games`. */
export async function fetchGames(): Promise<AdminGame[]> {
  if (mock.MOCK_MODE) return mock.settle(mock.games);
  const body = await http.get<{ games: AdminGame[] }>('/internal/admin/games');
  return body.games;
}

/**
 * MOCKED — `PATCH /internal/admin/games/:slug`.
 *
 * Note what is NOT here: the payout table is read-only in this panel. Edge is
 * realised by the payout table (CLAUDE.md rule 5 and the ADR behind it), so a
 * table edit is a mathematics change that belongs in a reviewed migration with
 * its edge re-derived — not in a text box an operator can nudge at 2am.
 */
export async function updateGame(slug: string, patch: GameSettingsPatch): Promise<AdminGame> {
  if (mock.MOCK_MODE) {
    const row = mock.games.find((g) => g.slug === slug);
    if (!row) throw new Error('Game not found.');
    if (patch.enabled !== undefined) row.enabled = patch.enabled;
    if (patch.minStake !== undefined) row.minStake = patch.minStake;
    if (patch.maxStake !== undefined) row.maxStake = patch.maxStake;
    if (patch.maxPayout !== undefined) row.maxPayout = patch.maxPayout;
    return mock.settle(row);
  }
  return http.patch<AdminGame>(`/internal/admin/games/${slug}`, patch);
}

// ----------------------------------------------------------- the house edge

/**
 * The one thing this panel must not invent.
 *
 * A house-edge plan is a mathematical claim — "these multipliers realise 4.00%"
 * — proved on the server against each game's own `distribution()`. A mock would
 * be a made-up claim about the mathematics of a live game, which is worse than
 * no screen. So in mock mode the plan comes back honestly un-editable, with the
 * reason, and the screen renders it the same way it renders a locked table game.
 */
function mockEdgePlan(slug: string): AdminEdgePlan {
  const row = mock.games.find((g) => g.slug === slug);
  return {
    slug,
    name: row?.name ?? slug,
    kind: row?.kind ?? 'round',
    model: 'unsupported',
    editable: false,
    reason:
      'The recompute runs on the server, against the game\'s own distribution — it is never ' +
      'computed in the browser. Start the panel with VITE_ADMIN_MOCK=0 to reach it.',
    minEdgePct: '1.00',
    maxEdgePct: '8.00',
    recordedEdgePct: row ? (Number(row.targetEdge) * 100).toFixed(4) : '0.0000',
    currentEdges: [],
    realisedHoldPct: mock.gameHold.find((g) => g.slug === slug)?.realisedHoldPct ?? null,
    turnover: mock.gameHold.find((g) => g.slug === slug)?.turnover ?? '0.0000',
    betCount: mock.gameHold.find((g) => g.slug === slug)?.betCount ?? 0,
    requestedEdgePct: '',
    changes: [],
    heldKeys: [],
    constant: null,
    verification: {
      ok: false,
      worstEdgePct: '0.0000',
      bestEdgePct: '0.0000',
      truncationBoundPct: '0.0000',
      after: [],
      failures: [],
    },
    selectionCount: 0,
    ceilings: [],
    maxReturn: null,
    overridden: false,
    pendingEdgePct: null,
    payoutTableAfter: row?.payoutTable ?? null,
    planToken: '',
  };
}

/**
 * LIVE — `GET /internal/admin/games/:slug/edge`.
 *
 * With no `edge`, this is a read: the model, the bounds, what the table realises
 * today and the realised hold beside it. With one, it is the full preview —
 * every multiplier that would move, what each bet would then realise, and the
 * stake ceilings the max-win cap re-derives. It writes nothing either way.
 *
 * `admin` role only, including the read: the recompute is a modelling tool for
 * whoever is allowed to pull the lever, and it sits behind the same gate.
 */
export async function previewEdge(
  slug: string,
  edgeFraction?: string,
  window: MetricsWindow = '24h',
): Promise<AdminEdgePlan> {
  if (mock.MOCK_MODE) return mock.settle(mockEdgePlan(slug));
  return http.get<AdminEdgePlan>(`/internal/admin/games/${slug}/edge`, {
    ...(edgeFraction === undefined ? {} : { edge: edgeFraction }),
    window,
  });
}

/**
 * LIVE — `POST /internal/admin/games/:slug/edge`.
 *
 * `planToken` is the fingerprint of the exact table the operator was shown. The
 * server recomputes and refuses with a 409 if the answer is no longer that
 * table, so what is confirmed is the DIFF and not the number.
 */
export async function applyEdge(
  slug: string,
  edgeFraction: string,
  planToken: string,
  reason: string,
): Promise<AdminEdgeApplied> {
  if (mock.MOCK_MODE) throw new Error('The edge editor needs the live API. Start with VITE_ADMIN_MOCK=0.');
  return http.post<AdminEdgeApplied>(`/internal/admin/games/${slug}/edge`, {
    edge: edgeFraction,
    planToken,
    reason,
  });
}

/**
 * LIVE — `DELETE /internal/admin/games/:slug/edge?scope=…`.
 *
 * `pending` drops a staged change that never reached a round boundary, so
 * nothing was ever live and nothing is undone. `override` hands the game back to
 * the seed: the row keeps its current prices — repricing a live game is what the
 * staging boundary is for and this is not that — and the next deploy restores
 * the catalogue's own table.
 */
export async function cancelEdge(slug: string, scope: 'pending' | 'override'): Promise<AdminEdgePlan> {
  if (mock.MOCK_MODE) throw new Error('The edge editor needs the live API. Start with VITE_ADMIN_MOCK=0.');
  return http.del<AdminEdgePlan>(`/internal/admin/games/${slug}/edge`, { scope });
}

// ----------------------------------------------------------- payment methods

/**
 * MOCKED — `GET /internal/admin/payment-methods`.
 *
 * Returns the registry AND `options`: the chain, asset, region and currency
 * sets the create route validates against, derived server-side from the ADR-021
 * chain table on every request. The panel renders those and keeps no list of
 * its own — a second copy is what let the create form send no currency, chain,
 * asset or region at all, so a crypto rail could not be registered from the
 * panel and a non-INR rail could not be registered at all (T-192).
 */
export async function fetchPaymentMethods(): Promise<PaymentMethodRegistry> {
  if (mock.MOCK_MODE) {
    return mock.settle({ methods: mock.paymentMethods, options: mock.paymentMethodOptions });
  }
  return http.get<PaymentMethodRegistry>('/internal/admin/payment-methods');
}

/**
 * LIVE — `POST /internal/admin/payment-methods`.
 *
 * The address is set here exactly once; the PATCH below deliberately cannot
 * touch it. A retired or compromised account is disabled and replaced with a
 * new method, never repointed (retire-and-replace).
 */
export async function createPaymentMethod(input: PaymentMethodCreate): Promise<AdminPaymentMethod> {
  if (mock.MOCK_MODE) {
    const nextId = Math.max(...mock.paymentMethods.map((m) => m.id)) + 1;
    const row: AdminPaymentMethod = {
      id: nextId,
      kind: input.kind,
      currency: input.currency ?? 'INR',
      chain: input.chain ?? null,
      asset: input.asset ?? null,
      region: input.region ?? 'IN',
      label: input.label,
      ownerEntity: input.ownerEntity,
      addressMasked: input.address.length <= 8
        ? input.address
        : `${input.address.slice(0, 4)}…${input.address.slice(-4)}`,
      displayName: input.displayName ?? null,
      enabled: true,
      health: 'healthy',
      weight: input.weight ?? 100,
      dailyLimit: input.dailyLimit,
      feePct: input.feePct ?? '0.0000',
      feeFixed: input.feeFixed ?? '0.0000',
      usedToday: '0.0000',
      usageDate: new Date().toISOString(),
      depositsToday: 0,
    };
    mock.paymentMethods.push(row);
    return mock.settle(row);
  }
  return http.post<AdminPaymentMethod>('/internal/admin/payment-methods', input);
}

/** MOCKED — `PATCH /internal/admin/payment-methods/:id`. */
export async function updatePaymentMethod(
  id: number,
  patch: PaymentMethodPatch,
): Promise<AdminPaymentMethod> {
  if (mock.MOCK_MODE) {
    const row = mock.paymentMethods.find((m) => m.id === id);
    if (!row) throw new Error('Payment method not found.');
    if (patch.enabled !== undefined) row.enabled = patch.enabled;
    if (patch.health !== undefined) row.health = patch.health;
    if (patch.weight !== undefined) row.weight = patch.weight;
    if (patch.dailyLimit !== undefined) row.dailyLimit = patch.dailyLimit;
    if (patch.feePct !== undefined) row.feePct = patch.feePct;
    if (patch.feeFixed !== undefined) row.feeFixed = patch.feeFixed;
    return mock.settle(row);
  }
  return http.patch<AdminPaymentMethod>(`/internal/admin/payment-methods/${id}`, patch);
}


// ------------------------------------------------------------------ secrets
//
// The key manager (T-143). Every one of these is LIVE — the endpoints exist.
//
// **Read the write path carefully.** `setSecret` is the only function in this
// file that sends a credential, and it is deliberately NOT mocked: in mock mode
// it still calls the real API and fails honestly. A form that appears to store
// a key and quietly does not is worse than a form that errors, because the
// operator walks away believing the rail is fixed.
//
// Nothing here returns a value. The server's response carries `last4` and a
// fingerprint, and there is no field in `SecretView` a key could arrive in.

/** LIVE — `GET /internal/admin/secrets`. Reads are mocked for `pnpm dev` only. */
export async function fetchSecrets(): Promise<SecretView[]> {
  if (mock.MOCK_MODE) return mock.settle(mock.secrets);
  const body = await http.get<{ secrets: SecretView[] }>('/internal/admin/secrets');
  return body.secrets;
}

/** LIVE — `GET /internal/admin/secrets/:name/history`. The append-only record. */
export async function fetchSecretHistory(name: string): Promise<SecretVersion[]> {
  if (mock.MOCK_MODE) return mock.settle([]);
  const body = await http.get<{ name: string; versions: SecretVersion[] }>(
    `/internal/admin/secrets/${encodeURIComponent(name)}/history`,
  );
  return body.versions;
}

/**
 * LIVE, ALWAYS — `POST /internal/admin/secrets/:name`.
 *
 * Throws `ApiError('secret_rejected')` when the pre-activation check refuses
 * the value, in which case **nothing was written and the old key is still
 * live**. That is the case the screen must state plainly rather than showing a
 * generic failure: the operator needs to know the rail did not just break.
 */
export async function setSecret(
  name: string,
  value: string,
  note?: string,
): Promise<SecretSetResult> {
  return http.post<SecretSetResult>(`/internal/admin/secrets/${encodeURIComponent(name)}`, {
    value,
    ...(note ? { note } : {}),
  });
}

/** LIVE, ALWAYS — `POST /internal/admin/secrets/:name/check`. One real call, changes nothing. */
export async function checkSecret(name: string): Promise<SecretValidation> {
  return http.post<SecretValidation>(`/internal/admin/secrets/${encodeURIComponent(name)}/check`);
}

/** LIVE, ALWAYS — `POST /internal/admin/secrets/:name/revoke`. Falls back to the environment. */
export async function revokeSecret(name: string, note?: string): Promise<SecretView> {
  return http.post<SecretView>(`/internal/admin/secrets/${encodeURIComponent(name)}/revoke`, {
    ...(note ? { note } : {}),
  });
}

// -------------------------------------------- analytics v2 (ADR-044)

/** LIVE — `GET /internal/admin/metrics/daily`. Reads are mocked for `pnpm dev` only. */
export async function fetchDailySeries(days: number): Promise<DailySeries> {
  if (mock.MOCK_MODE) {
    const series = mock.dailySeriesFor(days);
    return mock.settle({ from: series[0]?.day ?? '', to: series[series.length - 1]?.day ?? '', days: series });
  }
  const to = new Date().toISOString().slice(0, 10);
  const from = new Date(Date.now() - (days - 1) * 86400000).toISOString().slice(0, 10);
  return http.get<DailySeries>('/internal/admin/metrics/daily', { from, to });
}

/** LIVE — `GET /internal/admin/metrics/funnel`. */
export async function fetchFunnel(days: number): Promise<DepositFunnel> {
  if (mock.MOCK_MODE) return mock.settle(mock.funnelFor(days));
  const to = new Date().toISOString().slice(0, 10);
  const from = new Date(Date.now() - (days - 1) * 86400000).toISOString().slice(0, 10);
  return http.get<DepositFunnel>('/internal/admin/metrics/funnel', { from, to });
}

/** LIVE — `GET /internal/admin/metrics/cohorts`. */
export async function fetchCohorts(weeks = 8): Promise<RetentionCohort[]> {
  if (mock.MOCK_MODE) return mock.settle(mock.cohorts);
  const body = await http.get<{ cohorts: RetentionCohort[] }>('/internal/admin/metrics/cohorts', {
    weeks,
  });
  return body.cohorts;
}

/** LIVE — `GET /internal/admin/metrics/players`. The lifetime P&L list. */
export async function fetchPlayerLifetime(limit = 50): Promise<PlayerLifetime[]> {
  if (mock.MOCK_MODE) return mock.settle(mock.playerLifetime);
  const body = await http.get<{ players: PlayerLifetime[] }>('/internal/admin/metrics/players', {
    limit,
  });
  return body.players;
}

// ------------------------------------------------ audit read (ADR-044)

export interface AuditQuery {
  operatorId?: number;
  /** Prefix match on the dotted verb — `game.` catches the family. */
  action?: string;
  from?: string;
  to?: string;
  limit?: number;
  /** Keyset cursor: read strictly backwards from this id. */
  before?: number;
}

/** LIVE — `GET /internal/admin/audit`. The append-only log, filtered and paginated. */
export async function fetchAudit(query: AuditQuery = {}): Promise<AuditEntry[]> {
  if (mock.MOCK_MODE) {
    let rows = mock.auditEntries;
    if (query.action) rows = rows.filter((e) => e.action.startsWith(query.action!));
    if (query.before !== undefined) rows = rows.filter((e) => e.id < query.before!);
    return mock.settle(rows.slice(0, query.limit ?? 50));
  }
  const body = await http.get<{ entries: AuditEntry[] }>('/internal/admin/audit', {
    operatorId: query.operatorId,
    action: query.action || undefined,
    from: query.from || undefined,
    to: query.to || undefined,
    limit: query.limit,
    before: query.before,
  });
  return body.entries;
}

// ------------------------------------- UPI reconciliation (ADR-044)

/** LIVE — `GET /internal/admin/reconciliation/report`. Computed fresh per call. */
export async function fetchReconciliationReport(): Promise<ReconciliationReport> {
  if (mock.MOCK_MODE) return mock.settle(mock.reconciliationReport);
  return http.get<ReconciliationReport>('/internal/admin/reconciliation/report');
}

/**
 * LIVE, ALWAYS — `POST /internal/admin/reconciliation/import`.
 *
 * Never mocked, the secret-write rule: an import that appears to land a bank
 * statement and lands nothing is the worst thing this screen could fake. In
 * mock mode the call goes to the real endpoint and fails honestly.
 *
 * The server-side matcher PROPOSES only. Crediting a matched deposit stays on
 * the Deposits screen, where it is a named operator's audited confirmation.
 */
export async function importBankStatement(csv: string, label?: string): Promise<ReconImportResult> {
  return http.post<ReconImportResult>('/internal/admin/reconciliation/import', {
    csv,
    ...(label === undefined || label === '' ? {} : { label }),
  });
}

// -------------------------------------------------------------------- NL-BI

/**
 * LIVE — `POST /internal/admin/ai/query` (ADR-057). Admin-gated on the
 * server; registered only when the AI layer's env kill is on, so a 404 here
 * means the layer is off, not broken. Mock mode states that plainly instead
 * of inventing an answer — an invented figure from an "AI" screen is exactly
 * the thing ADR-058 exists to prevent.
 */
export async function askData(question: string): Promise<AiAskResult> {
  if (mock.MOCK_MODE) {
    return mock.settle({
      status: 'refused' as const,
      reason: 'Mock mode has no AI layer — this screen answers only against the live API.',
      sql: null,
    });
  }
  return http.post<AiAskResult>('/internal/admin/ai/query', { question });
}

/**
 * LIVE — `GET /internal/admin/ai/runs?agent=nlbi` (ADR-054). The ask screen's
 * history: only the per-ask summary rows (`refMeta.kind === 'nlbi_result'`),
 * newest first — generation rows stay on the Audit surface.
 */
export async function fetchAskHistory(limit = 30): Promise<AiRunRow[]> {
  if (mock.MOCK_MODE) return mock.settle([]);
  const body = await http.get<{ runs: AiRunRow[] }>('/internal/admin/ai/runs', {
    agent: 'nlbi',
    limit: 100,
  });
  return body.runs
    .filter((r) => (r.refMeta as { kind?: string } | null)?.kind === 'nlbi_result')
    .slice(0, limit);
}

// -------------------------------------------------------------- settings

/**
 * LIVE — `GET /internal/admin/settings` (ADR-045). The platform policy store.
 * Read to show the four-eyes threshold beside the withdrawal queue.
 */
export async function fetchSettings(): Promise<PlatformSetting[]> {
  if (mock.MOCK_MODE) return mock.settle(mock.settings);
  const body = await http.get<{ settings: PlatformSetting[] }>('/internal/admin/settings');
  return body.settings;
}

/** LIVE — `PUT /internal/admin/settings/:key`. Admin only; zod-validated server-side. */
export async function updateSetting(key: string, value: unknown): Promise<PlatformSetting> {
  if (mock.MOCK_MODE) return mock.settle(mock.updateSettingMock(key, value, currentOperator()?.id ?? 0));
  const body = await http.put<{ key: string; value: unknown }>(`/internal/admin/settings/${key}`, { value });
  return { key: body.key, value: body.value, updatedBy: currentOperator()?.id ?? 0, updatedAt: new Date().toISOString() };
}

// ------------------------------------------------------------------- MFA

/** LIVE — `GET /operator/mfa` (ADR-046). Whether the signed-in operator is enrolled. */
export async function fetchMfaStatus(): Promise<MfaStatus> {
  if (mock.MOCK_MODE) return mock.settle({ enrolled: false });
  return http.get<MfaStatus>('/operator/mfa');
}

/** LIVE — `POST /operator/mfa/enroll`. Returns the one-time secret and otpauth URI. */
export async function enrollMfa(): Promise<MfaEnrollment> {
  if (mock.MOCK_MODE) {
    return mock.settle({
      secret: 'JBSWY3DPEHPK3PXP',
      uri: 'otpauth://totp/Kismat%20Operator:demo?secret=JBSWY3DPEHPK3PXP&issuer=Kismat%20Operator&algorithm=SHA1&digits=6&period=30',
    });
  }
  return http.post<MfaEnrollment>('/operator/mfa/enroll');
}

/** LIVE — `POST /operator/mfa/confirm`. Proves one code and enables the second factor. */
export async function confirmMfa(totpCode: string): Promise<{ enabledAt: string }> {
  if (mock.MOCK_MODE) return mock.settle({ enabledAt: new Date().toISOString() });
  return http.post<{ enabledAt: string }>('/operator/mfa/confirm', { totpCode });
}

// -------------------------------------------------- games: bulk availability

/**
 * LIVE — bulk enable/disable, as a loop over the documented, audited
 * `PATCH /internal/admin/games/:slug`. One audit entry per game (which is the
 * more traceable choice than a single opaque "all" write), reason recorded on
 * each. Returns the count changed and any that failed, so the panel reports the
 * truth rather than a blanket "done".
 */
export async function bulkSetGamesEnabled(
  slugs: string[],
  enabled: boolean,
): Promise<{ changed: number; failed: Array<{ slug: string; error: string }> }> {
  const failed: Array<{ slug: string; error: string }> = [];
  let changed = 0;
  for (const slug of slugs) {
    try {
      await updateGame(slug, { enabled });
      changed += 1;
    } catch (cause) {
      failed.push({ slug, error: cause instanceof Error ? cause.message : 'failed' });
    }
  }
  return { changed, failed };
}

// -------------------------------------------------- seated bot difficulty (OC1)

export type BotDifficulty = 'easy' | 'normal' | 'sharp';

/**
 * LIVE — the global default seated-bot skill, read from `platform_settings`
 * (`bot_difficulty`). `normal` when unset. Tunes move SELECTION only; the dice
 * stay `crypto.randomInt` at every tier (rule 5).
 */
export async function fetchBotDifficulty(): Promise<BotDifficulty> {
  if (mock.MOCK_MODE) return 'normal';
  const body = await http.get<{ key: string; value: { default: BotDifficulty } | null }>(
    '/internal/admin/settings/bot_difficulty',
  );
  return body.value?.default ?? 'normal';
}

/** LIVE — `PUT /internal/admin/settings/bot_difficulty`. Admin only; audited. */
export async function updateBotDifficulty(difficulty: BotDifficulty): Promise<BotDifficulty> {
  if (mock.MOCK_MODE) throw new Error('Bot difficulty needs the live API. Start with VITE_ADMIN_MOCK=0.');
  const body = await http.put<{ key: string; value: { default: BotDifficulty } }>(
    '/internal/admin/settings/bot_difficulty',
    { value: { default: difficulty } },
  );
  return body.value.default;
}

// -------------------------------------------------- house-bot liveness (OC2b)

/**
 * House-bot lobby liveness (OC2b, ADR-060). House-funded, `is_bot`-flagged
 * accounts place REAL bets in house-banked games so tables and feeds are not
 * empty at launch. DISABLED by default. The owner's analytics EXCLUDE every bot,
 * so this can never fool the business numbers; `showInFeed` toggles whether a
 * bot's (real, masked) win may appear in the public wins feed.
 */
export interface LivenessSettings {
  enabled: boolean;
  botCount: number;
  stakeMinInr: number;
  stakeMaxInr: number;
  betsPerTick: number;
  games: string[];
  showInFeed: boolean;
}

const LIVENESS_DEFAULTS: LivenessSettings = {
  enabled: false,
  botCount: 6,
  stakeMinInr: 10,
  stakeMaxInr: 200,
  betsPerTick: 3,
  games: ['head_tail', 'casino_dice', 'limbo', 'dice_rolling', 'crash'],
  showInFeed: true,
};

/** LIVE — `GET /internal/admin/settings/liveness`. Defaults (DISABLED) when unset. */
export async function fetchLiveness(): Promise<LivenessSettings> {
  if (mock.MOCK_MODE) return LIVENESS_DEFAULTS;
  const body = await http.get<{ key: string; value: Partial<LivenessSettings> | null }>(
    '/internal/admin/settings/liveness',
  );
  return { ...LIVENESS_DEFAULTS, ...(body.value ?? {}) };
}

/** LIVE — `PUT /internal/admin/settings/liveness`. Admin only; audited. */
export async function updateLiveness(value: LivenessSettings): Promise<LivenessSettings> {
  if (mock.MOCK_MODE) throw new Error('Liveness needs the live API. Start with VITE_ADMIN_MOCK=0.');
  const body = await http.put<{ key: string; value: LivenessSettings }>(
    '/internal/admin/settings/liveness',
    { value },
  );
  return { ...LIVENESS_DEFAULTS, ...body.value };
}

// ------------------------------------------- payment limits (T-192, ADR-045)

/**
 * The per-transaction deposit and withdrawal bounds and the payout fee, held in
 * `platform_settings.payment_limits`.
 *
 * The defaults below are the server's own — the exact figures that were hard
 * coded in `deposits.service.ts` and `withdrawals.service.ts` until T-192 — so
 * a platform that has never written the key reads the same numbers it enforces.
 * Amounts are decimal STRINGS end to end: the panel never turns a bound into a
 * JS number, because the server compares it against a real amount.
 */
export const PAYMENT_LIMIT_DEFAULTS: PaymentLimits = {
  minDepositInr: '100.0000',
  maxDepositInr: '200000.0000',
  minWithdrawalInr: '500.0000',
  maxWithdrawalInr: '100000.0000',
  withdrawalFeeInr: '0.0000',
};

/** LIVE — `GET /internal/admin/settings/payment_limits`. Server defaults when unset. */
export async function fetchPaymentLimits(): Promise<PaymentLimits> {
  if (mock.MOCK_MODE) return mock.settle(PAYMENT_LIMIT_DEFAULTS);
  const body = await http.get<{ key: string; value: PaymentLimits | null }>(
    '/internal/admin/settings/payment_limits',
  );
  return body.value ?? PAYMENT_LIMIT_DEFAULTS;
}

/**
 * LIVE — `PUT /internal/admin/settings/payment_limits`. Admin only; audited.
 *
 * The WHOLE set is sent every time. The server validates a settings value in
 * isolation, and these rules are relations between the five figures, so a
 * partial write could raise a minimum past a maximum written earlier and pass.
 */
export async function updatePaymentLimits(value: PaymentLimits): Promise<PaymentLimits> {
  if (mock.MOCK_MODE) {
    throw new Error('Payment limits need the live API. Start with VITE_ADMIN_MOCK=0.');
  }
  const body = await http.put<{ key: string; value: PaymentLimits }>(
    '/internal/admin/settings/payment_limits',
    { value },
  );
  return body.value;
}

// ----------------------------------------- adaptive bot coach (OC2a, ADR-059)

/**
 * The five ramp thresholds the OC2a bot coach tunes on. All optional on the
 * wire — the server fills any the operator leaves out with its defaults. INR,
 * because every seated lane is INR.
 */
export interface BotCoachThresholds {
  newcomerGames: number;
  newcomerAgeDays: number;
  veteranGames: number;
  winningNetInr: number;
  strugglingNetInr: number;
}

/** Whether the `bot_coach` AI agent is enabled (its `ai_config` row). */
export async function fetchBotCoachEnabled(): Promise<boolean> {
  if (mock.MOCK_MODE) return false;
  const body = await http.get<{ agents: Array<{ agent: string; enabled: boolean }> }>(
    '/internal/admin/ai/agents',
  );
  return body.agents.find((a) => a.agent === 'bot_coach')?.enabled ?? false;
}

/** LIVE — `PUT /internal/admin/ai/agents/bot_coach`. Admin only; audited. Also needs AI_OPS_ENABLED on the server. */
export async function setBotCoachEnabled(enabled: boolean): Promise<boolean> {
  if (mock.MOCK_MODE) throw new Error('The bot coach needs the live API. Start with VITE_ADMIN_MOCK=0.');
  const body = await http.put<{ agent: string; enabled: boolean }>(
    '/internal/admin/ai/agents/bot_coach',
    { enabled },
  );
  return body.enabled;
}

/** The coach thresholds, or null when none are stored (server defaults apply). */
export async function fetchBotCoachThresholds(): Promise<Partial<BotCoachThresholds> | null> {
  if (mock.MOCK_MODE) return null;
  const body = await http.get<{ key: string; value: Partial<BotCoachThresholds> | null }>(
    '/internal/admin/settings/bot_coach',
  );
  return body.value;
}

/** LIVE — `PUT /internal/admin/settings/bot_coach`. Admin only; audited. */
export async function updateBotCoachThresholds(
  thresholds: Partial<BotCoachThresholds>,
): Promise<Partial<BotCoachThresholds>> {
  if (mock.MOCK_MODE) throw new Error('The bot coach needs the live API. Start with VITE_ADMIN_MOCK=0.');
  const body = await http.put<{ key: string; value: Partial<BotCoachThresholds> }>(
    '/internal/admin/settings/bot_coach',
    { value: thresholds },
  );
  return body.value;
}

// ------------------------------------------------------------- promotions (OC1)

/** LIVE — `GET /internal/admin/promos/campaigns` (ADR-038). */
export async function fetchCampaigns(): Promise<PromoCampaign[]> {
  if (mock.MOCK_MODE) return mock.settle([]);
  const body = await http.get<{ campaigns: PromoCampaign[] }>('/internal/admin/promos/campaigns');
  return body.campaigns;
}

/** LIVE — `POST /internal/admin/promos/campaigns`. Admin only; audited. */
export async function createCampaign(input: PromoCampaignCreate): Promise<PromoCampaign> {
  if (mock.MOCK_MODE) throw new Error('Promotions need the live API. Start with VITE_ADMIN_MOCK=0.');
  const body = await http.post<{ campaign: PromoCampaign }>('/internal/admin/promos/campaigns', input);
  return body.campaign;
}

/** LIVE — `PATCH /internal/admin/promos/campaigns/:id`. Admin only; audited. */
export async function updateCampaign(id: number, patch: PromoCampaignPatch): Promise<PromoCampaign> {
  if (mock.MOCK_MODE) throw new Error('Promotions need the live API. Start with VITE_ADMIN_MOCK=0.');
  const body = await http.patch<{ campaign: PromoCampaign }>(`/internal/admin/promos/campaigns/${id}`, patch);
  return body.campaign;
}

/** LIVE — `GET /internal/admin/promos/codes?campaignId=…`. */
export async function fetchCodes(campaignId?: number): Promise<PromoCode[]> {
  if (mock.MOCK_MODE) return mock.settle([]);
  const body = await http.get<{ codes: PromoCode[] }>('/internal/admin/promos/codes', campaignId ? { campaignId } : undefined);
  return body.codes;
}

/** LIVE — `POST /internal/admin/promos/codes`. Admin only; audited. */
export async function createCode(input: { campaignId: number; code: string; maxClaims?: number | null; enabled?: boolean }): Promise<PromoCode> {
  if (mock.MOCK_MODE) throw new Error('Promotions need the live API. Start with VITE_ADMIN_MOCK=0.');
  const body = await http.post<{ code: PromoCode }>('/internal/admin/promos/codes', input);
  return body.code;
}

/** LIVE — `PATCH /internal/admin/promos/codes/:id`. Admin only; audited. */
export async function updateCode(id: number, patch: { enabled?: boolean; maxClaims?: number | null }): Promise<PromoCode> {
  if (mock.MOCK_MODE) throw new Error('Promotions need the live API. Start with VITE_ADMIN_MOCK=0.');
  const body = await http.patch<{ code: PromoCode }>(`/internal/admin/promos/codes/${id}`, patch);
  return body.code;
}

/** LIVE — `GET /internal/admin/promos/weights` (ADR-038). Wagering game weights. */
export async function fetchPromoWeights(): Promise<PromoWeight[]> {
  if (mock.MOCK_MODE) return mock.settle([]);
  const body = await http.get<{ weights: PromoWeight[] }>('/internal/admin/promos/weights');
  return body.weights;
}

/** LIVE — `PATCH /internal/admin/promos/weights`. Admin only; audited. */
export async function updatePromoWeight(gameSlug: string, weightBps: number): Promise<PromoWeight> {
  if (mock.MOCK_MODE) throw new Error('Promotions need the live API. Start with VITE_ADMIN_MOCK=0.');
  const body = await http.patch<{ weight: PromoWeight }>('/internal/admin/promos/weights', { gameSlug, weightBps });
  return body.weight;
}

/** LIVE — `GET /internal/admin/promos/liability` (ADR-038). Outstanding bonus liability. */
export async function fetchPromoLiability(): Promise<PromoLiability> {
  if (mock.MOCK_MODE) return mock.settle({ perCurrency: [], byCampaign: [] });
  return http.get<PromoLiability>('/internal/admin/promos/liability');
}

/** LIVE — `GET /internal/admin/promos/vip-tiers` (ADR-040). The loyalty ladder. */
export async function fetchVipTiers(): Promise<VipTier[]> {
  if (mock.MOCK_MODE) return mock.settle([]);
  const body = await http.get<{ tiers: VipTier[] }>('/internal/admin/promos/vip-tiers');
  return body.tiers;
}

/** LIVE — `PATCH /internal/admin/promos/vip-tiers`. Admin only; audited. Upsert by tier. */
export async function upsertVipTier(input: VipTierUpsert): Promise<VipTier> {
  if (mock.MOCK_MODE) throw new Error('VIP tiers need the live API. Start with VITE_ADMIN_MOCK=0.');
  const body = await http.patch<{ tier: VipTier }>('/internal/admin/promos/vip-tiers', input);
  return body.tier;
}
